import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InputComponent } from '@shared/scenes/workflows/InputComponent';
import { useGetWorkflowTemplateLazyQuery } from '@shared/scenes/workflows/hooks';
import {
  getComponentIconWithFallback,
  getComponentLabelWithFallback,
  isGroupableComponent,
  isInputComponent,
  isRelatedCardComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { RelatedEntityContext, RelatedFilterOptions } from '../types';
import { toFilterOptions, toGroupOptions } from './toFilterOptions';

// Component types the filter UI can actually filter on. Nested options are restricted to these so
// expanding a related card can't produce a row with no way to pick a value - a related workflow
// may well have a filterable RELATED_CARD_LOOKUP, which has no editor. TIME_TRACKER qualifies only
// through its scalar representations; `toFilterOptions` drops its own value, which has no editor
// (see `canFilterComponentValue`).
const SUPPORTED_NESTED_FILTER_TYPES: ReadonlySet<string> = new Set([
  'DATE',
  'EMAIL',
  'FILE',
  'NUMBER',
  'PERSON',
  'RELATED_CARD',
  'SIGNATURE',
  'TAG',
  'TEXT',
  'TIME_TRACKER',
  'URL',
]);

interface RelatedFilterOptionsResult {
  /** Filterable components on each requested related workflow, keyed by related-card component id. */
  relatedFilters: RelatedFilterOptions;
  /** The groupable subset (tag / person / related card). */
  relatedGroups: RelatedFilterOptions;
  requestRelatedFilterOptions: (componentIds: string[]) => void;
}

/**
 * Resolves the nested filter/group options for cross-workflow filtering.
 *
 * `ControlsProvider` can't call a query hook per related-card component, so this fetches through
 * `useGetWorkflowTemplateLazyQuery` instead. That writes the same Apollo cache entry as
 * `useWorkflowTemplate(id)`, so anything reading the template later is a cache hit.
 *
 * Callers register demand rather than getting everything up front: a related workflow is only
 * fetched once a menu expands that related card, or once applied criteria turns out to reference
 * it (see `getRelatedEntityFilterComponentIds`).
 */
export function useRelatedFilterOptions(components: InputComponent[]): RelatedFilterOptionsResult {
  const getWorkflowTemplate = useGetWorkflowTemplateLazyQuery();
  const [requestedComponentIds, setRequestedComponentIds] = useState<string[]>([]);
  const [templates, setTemplates] = useState<Record<string, API.WorkflowTemplateFragment>>({});
  // Ids already fetched or in flight. A ref rather than state so a re-render mid-fetch can't
  // schedule the same request twice.
  const handledTemplateIds = useRef<Set<string>>(new Set());

  const requestRelatedFilterOptions = useCallback((componentIds: string[]) => {
    if (isEmpty(componentIds)) {
      return;
    }
    setRequestedComponentIds(prev => {
      const missing = componentIds.filter(id => !prev.includes(id));
      return isEmpty(missing) ? prev : [...prev, ...missing];
    });
  }, []);

  // Requested related-card component id -> the workflow template it points at. Keyed on ids only:
  // components aren't normalized in the Apollo cache, so a parent-template refetch hands back new
  // object identities for otherwise identical components.
  const requestedTemplateIdsByComponentId = useMemo(() => {
    const entries = requestedComponentIds.map((componentId): [string, string] | null => {
      const component = components.find(c => c.id === componentId);
      if (isNil(component) || !isRelatedCardComponent(component)) {
        return null;
      }
      const workflowTemplateId = component.workflowTemplateId;
      return isNil(workflowTemplateId) ? null : [componentId, workflowTemplateId];
    });
    return new Map(entries.filter((entry): entry is [string, string] => !isNil(entry)));
  }, [requestedComponentIds, components]);

  const requestedTemplateIds = useMemo(
    () => Array.from(new Set(requestedTemplateIdsByComponentId.values())),
    [requestedTemplateIdsByComponentId],
  );

  useEffect(() => {
    // `handledTemplateIds` makes this idempotent, so it doesn't matter that
    // `requestedTemplateIds` can get a fresh identity when the parent template refetches.
    const unfetched = requestedTemplateIds.filter(id => !handledTemplateIds.current.has(id));
    if (isEmpty(unfetched)) {
      return;
    }
    unfetched.forEach(id => handledTemplateIds.current.add(id));
    unfetched.forEach(id => {
      getWorkflowTemplate(id).then(template => {
        if (!isNil(template)) {
          setTemplates(prev => ({ ...prev, [id]: template }));
        }
      });
    });
  }, [requestedTemplateIds, getWorkflowTemplate]);

  const { relatedFilters, relatedGroups } = useMemo(() => {
    const filters: RelatedFilterOptions = new Map();
    const groups: RelatedFilterOptions = new Map();
    requestedTemplateIdsByComponentId.forEach((workflowTemplateId, componentId) => {
      const template = templates[workflowTemplateId];
      const parent = components.find(c => c.id === componentId);
      if (isNil(template) || isNil(parent)) {
        return;
      }
      const relatedEntity: RelatedEntityContext = {
        componentId,
        label: getComponentLabelWithFallback(parent),
        icon: getComponentIconWithFallback(parent),
        workflowTemplateId,
      };
      const nestedComponents = (template.components ?? []).filter(isInputComponent);
      filters.set(
        componentId,
        toFilterOptions(
          nestedComponents.filter(component => SUPPORTED_NESTED_FILTER_TYPES.has(component.type)),
          relatedEntity,
        ),
      );
      groups.set(
        componentId,
        toGroupOptions(nestedComponents.filter(isGroupableComponent), relatedEntity),
      );
    });
    return { relatedFilters: filters, relatedGroups: groups };
  }, [requestedTemplateIdsByComponentId, templates, components]);

  return useMemo(
    () => ({
      relatedFilters,
      relatedGroups,
      requestRelatedFilterOptions,
    }),
    [relatedFilters, relatedGroups, requestRelatedFilterOptions],
  );
}
